import { useState, useEffect, useCallback } from 'react';

/**
 * useOfflineQueue (Tier 4.2)
 *
 * Reads the offline invoice queue from IndexedDB (written by the service worker)
 * and exposes it to React components for UI (pending sync badge, offline banner).
 *
 * Returns:
 *   isOnline    — current network status
 *   queueCount  — number of invoices pending sync
 *   queuedItems — full array of queued request descriptors
 */

const DB_NAME = 'gst-offline-queue';
const DB_VER  = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore('requests', { keyPath: 'idempotencyKey' });
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function readAllQueued() {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx  = db.transaction('requests', 'readonly');
      const req = tx.objectStore('requests').getAll();
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror   = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export function useOfflineQueue() {
  const [isOnline,    setIsOnline]    = useState(navigator.onLine);
  const [queuedItems, setQueuedItems] = useState([]);

  const refreshQueue = useCallback(async () => {
    const items = await readAllQueued();
    setQueuedItems(items);
  }, []);

  useEffect(() => {
    refreshQueue();

    const handleOnline  = () => { setIsOnline(true);  refreshQueue(); };
    const handleOffline = () => { setIsOnline(false); };

    const handleSwMessage = (event) => {
      if (
        event.data?.type === 'QUEUE_UPDATED' ||
        event.data?.type === 'SYNC_SUCCESS'
      ) {
        refreshQueue();
      }
    };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
  }, [refreshQueue]);

  return {
    isOnline,
    queuedItems,
    queueCount: queuedItems.length,
  };
}
